import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtUSD, fmtDate, PARTNER_LABELS } from "@/lib/format";
import { Sparkles, Package, Gem, TrendingUp, ArrowRight, PiggyBank } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try { const { data } = await api.get("/dashboard"); setData(data); }
            catch { toast.error("Failed to load dashboard"); }
            finally { setLoading(false); }
        })();
    }, []);

    if (loading) return <div className="text-stone-500 text-sm uppercase tracking-widest" data-testid="dashboard-loading">Loading…</div>;
    if (!data) return null;

    const { ledger, counts, inventory, recent_sales, recent_expenses } = data;
    const activeStones = (inventory.parcel_status.available || 0) + (inventory.parcel_status.in_production || 0) + (inventory.parcel_status.set_in_jewelry || 0);

    return (
        <div className="space-y-10 animate-fade-up" data-testid="dashboard-page">
            <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                <div>
                    <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-2">Overview</div>
                    <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-none text-stone-900">Partnership at a glance</h1>
                </div>
                <div className="text-xs uppercase tracking-widest text-stone-500">
                    {counts.root_parcels} root parcels · {counts.parcels} total · {counts.jewelry} jewelry · {counts.sales} sales
                </div>
            </header>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Kpi icon={<Sparkles className="h-4 w-4" strokeWidth={1.5} />} label="Inventory value (at cost)" value={fmtUSD(inventory.value_at_cost)} sub={`${activeStones} parcels + ${(inventory.jewelry_status.in_production || 0) + (inventory.jewelry_status.available || 0)} jewelry active`} testid="kpi-inventory-value" />
                <Kpi icon={<TrendingUp className="h-4 w-4" strokeWidth={1.5} />} label="Net profit realised" value={fmtUSD(ledger.totals.total_net_profit)} sub={`revenue ${fmtUSD(ledger.totals.total_revenue)}`} testid="kpi-net-profit" accent />
                <Kpi icon={<Package className="h-4 w-4" strokeWidth={1.5} />} label="Capital deployed" value={fmtUSD(ledger.totals.total_capital_deployed)} sub={`expenses ${fmtUSD(ledger.totals.total_expenses)}`} testid="kpi-capital" />
                <Kpi icon={<Gem className="h-4 w-4" strokeWidth={1.5} />} label="Active inventory" value={`${activeStones + (inventory.jewelry_status.in_production || 0) + (inventory.jewelry_status.available || 0)} items`} sub={`sold ${inventory.parcel_status.sold || 0} parcels / ${inventory.jewelry_status.sold || 0} jewelry`} testid="kpi-active-inventory" />
            </div>

            <FairnessBanner fairness={ledger.funding_fairness} />

            <section>
                <div className="flex items-baseline justify-between mb-4">
                    <h2 className="font-display text-2xl text-stone-900">Partner positions</h2>
                    <Link to="/ledger" className="text-xs uppercase tracking-widest text-stone-500 hover:text-emerald-700 transition-colors" data-testid="see-full-ledger">See full ledger →</Link>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {["bashari", "ido"].map(pk => <PartnerCard key={pk} partner={pk} row={ledger.partners[pk]} />)}
                </div>
            </section>

            <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <RecentPanel title="Recent sales" empty="No sales yet" testid="recent-sales"
                    rows={recent_sales.map(s => ({
                        left: fmtDate(s.sale_date),
                        mid: `${fmtUSD(s.sale_price)} · ${s.target_type}`,
                        right: s.breakdown?.is_loss ? "loss" : `+${fmtUSD(s.breakdown?.net_profit || 0)}`,
                        accent: !s.breakdown?.is_loss,
                    }))} />
                <RecentPanel title="Recent expenses" empty="No expenses logged" testid="recent-expenses"
                    rows={recent_expenses.map(e => ({
                        left: fmtDate(e.date),
                        mid: `${e.category} · ${PARTNER_LABELS[e.paid_by]}`,
                        right: fmtUSD(e.amount),
                        accent: false,
                    }))} />
            </section>
        </div>
    );
}

const Kpi = ({ icon, label, value, sub, testid, accent }) => (
    <div className="border border-stone-200 bg-white p-5 transition-all hover:border-stone-400 hover:-translate-y-0.5 duration-300" data-testid={testid}>
        <div className="flex items-center justify-between text-stone-500">
            <div className="text-[10px] uppercase tracking-widest">{label}</div>
            <span className={accent ? "text-emerald-700" : "text-stone-500"}>{icon}</span>
        </div>
        <div className={`font-light num text-2xl mt-3 ${accent ? "text-emerald-800" : "text-stone-900"}`}>{value}</div>
        <div className="text-[11px] text-stone-500 mt-1 uppercase tracking-wider">{sub}</div>
    </div>
);

const FairnessBanner = ({ fairness }) => {
    if (!fairness || !(Number(fairness.amount || 0) > 0.005)) return null;
    return (
        <Link to="/ledger" className="block border-2 border-sky-500/50 bg-sky-50 hover:bg-sky-100 transition-colors p-4 sm:p-5" data-testid="dashboard-fairness-banner">
            <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2 text-sky-800">
                    <PiggyBank className="h-4 w-4" strokeWidth={1.5} />
                    <span className="text-[10px] uppercase tracking-widest">Funding equalization</span>
                </div>
                <div className="flex items-center gap-3 flex-wrap text-stone-900">
                    <span className="font-display text-xl">{PARTNER_LABELS[fairness.debtor]}</span>
                    <span className="text-xs uppercase tracking-widest text-stone-500">owes</span>
                    <span className="font-display text-xl">{PARTNER_LABELS[fairness.creditor]}</span>
                    <ArrowRight className="h-4 w-4 text-stone-500" />
                    <span className="font-display text-2xl text-sky-700 num" data-testid="dashboard-fairness-amount">{fmtUSD(fairness.amount)}</span>
                </div>
                <div className="ml-auto text-xs text-stone-500 uppercase tracking-widest hidden md:block">
                    to equalize live capital →
                </div>
            </div>
        </Link>
    );
};

const PartnerCard = ({ partner, row }) => (
    <div className="border border-stone-200 bg-white p-6 hover:border-emerald-500/50 transition-colors duration-300" data-testid={`partner-card-${partner}`}>
        <div className="flex items-baseline justify-between">
            <div>
                <div className="text-[10px] uppercase tracking-widest text-stone-500">Partner</div>
                <div className="font-display text-3xl text-stone-900">{PARTNER_LABELS[partner]}</div>
            </div>
            <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-stone-500">Net owed</div>
                <div className="num text-2xl font-light text-emerald-700" data-testid={`partner-${partner}-net-owed`}>{fmtUSD(row.net_owed)}</div>
            </div>
        </div>
        <div className="grid grid-cols-3 gap-3 mt-6 text-xs">
            <Stat label="Capital in" value={fmtUSD(row.capital_contributed)} testid={`partner-${partner}-capital-in`} />
            <Stat label="Expenses in" value={fmtUSD(row.expenses_paid)} testid={`partner-${partner}-expenses-in`} />
            <Stat label="Profit earned" value={fmtUSD(row.profit_earned)} testid={`partner-${partner}-profit`} />
        </div>
    </div>
);

const Stat = ({ label, value, testid }) => (
    <div>
        <div className="text-[10px] uppercase tracking-widest text-stone-500">{label}</div>
        <div className="num text-stone-800" data-testid={testid}>{value}</div>
    </div>
);

const RecentPanel = ({ title, rows, empty, testid }) => (
    <div className="border border-stone-200 bg-white p-5" data-testid={testid}>
        <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-widest text-stone-500">{title}</div>
        </div>
        {rows.length === 0 ? (
            <div className="py-6 text-sm text-stone-500">{empty}</div>
        ) : (
            <ul className="divide-y divide-stone-200/70">
                {rows.map((r, i) => (
                    <li key={i} className="py-2.5 flex items-center justify-between text-sm">
                        <span className="text-stone-500 text-xs uppercase tracking-wider w-24 shrink-0">{r.left}</span>
                        <span className="text-stone-700 truncate flex-1 px-3">{r.mid}</span>
                        <span className={`num ${r.accent ? "text-emerald-700" : "text-stone-800"}`}>{r.right}</span>
                    </li>
                ))}
            </ul>
        )}
    </div>
);
